import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import { DnoStatusBadge } from './DnoStatusBadge';
import { WhatsAppComposeDialog } from './WhatsAppComposeDialog';
import {
  STATUS_DISPLAY_NAMES,
  STATUS_COLORS,
  formatDate,
  formatCurrency,
} from '../lib/utils';
import {
  User,
  Phone,
  Mail,
  MapPin,
  GripVertical,
  MessageCircle,
} from 'lucide-react';

// Pipeline stages for Kanban (exclude completed & archived)
const KANBAN_STAGES = [
  'lead',
  'qualified',
  'quote_sent',
  'approved',
  'deposit_taken',
  'scheduled',
  'ordered',
  'in_progress_installation',
  'in_progress_invoiced',
  'in_progress_handover',
];

const STAGE_ACCENT = {
  lead: 'border-t-slate-400',
  qualified: 'border-t-blue-500',
  quote_sent: 'border-t-purple-500',
  approved: 'border-t-amber-500',
  deposit_taken: 'border-t-indigo-500',
  scheduled: 'border-t-cyan-500',
  ordered: 'border-t-orange-500',
  in_progress_installation: 'border-t-yellow-500',
  in_progress_invoiced: 'border-t-yellow-600',
  in_progress_handover: 'border-t-yellow-700',
};

const CustomerCard = React.memo(({ customer, index, onWhatsApp }) => {
  const navigate = useNavigate();
  // Suppress click navigation if user actually dragged the card
  const dragStartRef = React.useRef({ x: 0, y: 0, dragging: false });

  const handlePointerDown = (e) => {
    dragStartRef.current = { x: e.clientX, y: e.clientY, dragging: false };
  };
  const handlePointerMove = (e) => {
    const dx = Math.abs(e.clientX - dragStartRef.current.x);
    const dy = Math.abs(e.clientY - dragStartRef.current.y);
    if (dx > 4 || dy > 4) dragStartRef.current.dragging = true;
  };
  const handleClick = () => {
    if (dragStartRef.current.dragging) return;
    navigate(`/customers/${customer.id}`);
  };

  return (
    <Draggable draggableId={customer.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onClick={handleClick}
          className={`group bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 mb-2 transition-shadow ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-amber-400/50 cursor-grabbing' : 'hover:shadow-md cursor-grab'
          }`}
          data-testid={`kanban-card-${customer.id}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">
                {customer.first_name} {customer.last_name}
              </p>
              {customer.system_total_cost > 0 && (
                <p className="text-xs font-semibold text-emerald-600 mt-0.5">
                  {formatCurrency(customer.system_total_cost)}
                </p>
              )}
            </div>
            <div
              className="flex-shrink-0 flex items-center gap-1"
            >
              {customer.phone && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onWhatsApp?.(customer); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-emerald-50 text-emerald-600"
                  title="Message on WhatsApp"
                  data-testid={`kanban-whatsapp-${customer.id}`}
                >
                  <MessageCircle className="w-4 h-4" />
                </button>
              )}
              <span className="opacity-0 group-hover:opacity-60 transition-opacity" aria-hidden="true">
                <GripVertical className="w-4 h-4 text-slate-400" />
              </span>
            </div>
          </div>

          <div className="mt-2 space-y-1">
            {customer.address && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500 truncate">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{customer.address}</span>
              </div>
            )}
            {customer.phone && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Phone className="w-3 h-3 flex-shrink-0" />
                <span>{customer.phone}</span>
              </div>
            )}
          </div>

          <div className="mt-2 flex items-center justify-between gap-1 flex-wrap">
            <span className="text-[10px] text-slate-400">
              {formatDate(customer.created_at)}
            </span>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <DnoStatusBadge customer={customer} />
              <PaymentStatusBadge customer={customer} variant="compact" />
              {customer.installation_type && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 capitalize">
                  {customer.installation_type}
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
});

CustomerCard.displayName = 'CustomerCard';

export const KanbanBoard = ({ customers, onStatusChange }) => {
  const [waCustomer, setWaCustomer] = useState(null);
  // Group customers by status
  const columns = {};
  for (const stage of KANBAN_STAGES) {
    columns[stage] = [];
  }
  for (const customer of customers) {
    if (columns[customer.status]) {
      columns[customer.status].push(customer);
    }
  }

  const handleDragEnd = useCallback(
    (result) => {
      const { destination, source, draggableId } = result;
      if (!destination) return;
      if (destination.droppableId === source.droppableId && destination.index === source.index) return;

      const newStatus = destination.droppableId;
      if (newStatus !== source.droppableId) {
        onStatusChange(draggableId, newStatus);
      }
    },
    [onStatusChange]
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div
        className="flex gap-3 overflow-x-auto pb-4"
        style={{ minHeight: 'calc(100vh - 260px)' }}
        data-testid="kanban-board"
      >
        {KANBAN_STAGES.map((stage) => {
          const items = columns[stage] || [];
          const totalValue = items.reduce((sum, c) => sum + (c.system_total_cost || 0), 0);

          return (
            <div
              key={stage}
              className={`flex-shrink-0 w-[260px] bg-slate-50/80 dark:bg-slate-900/80 rounded-xl border border-slate-200 dark:border-slate-700 flex flex-col border-t-4 ${STAGE_ACCENT[stage] || 'border-t-slate-300'}`}
              data-testid={`kanban-column-${stage}`}
            >
              {/* Column header */}
              <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide truncate">
                    {STATUS_DISPLAY_NAMES[stage]}
                  </h3>
                  <Badge
                    variant="secondary"
                    className="text-[10px] h-5 min-w-[20px] justify-center bg-slate-200 text-slate-600"
                  >
                    {items.length}
                  </Badge>
                </div>
                {totalValue > 0 && (
                  <p className="text-[10px] text-slate-400 font-medium">
                    {formatCurrency(totalValue)}
                  </p>
                )}
              </div>

              {/* Column body */}
              <Droppable droppableId={stage}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 p-2 min-h-[100px] transition-colors ${
                      snapshot.isDraggingOver ? 'bg-amber-50/50' : ''
                    }`}
                  >
                    <ScrollArea className="h-full max-h-[calc(100vh-360px)]">
                      {items.map((customer, index) => (
                        <CustomerCard key={customer.id} customer={customer} index={index} onWhatsApp={setWaCustomer} />
                      ))}
                      {provided.placeholder}
                      {items.length === 0 && !snapshot.isDraggingOver && (
                        <div className="flex items-center justify-center h-16 text-xs text-slate-400">
                          No customers
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>

      <WhatsAppComposeDialog
        open={!!waCustomer}
        onOpenChange={(o) => { if (!o) setWaCustomer(null); }}
        phone={waCustomer?.whatsapp_number || waCustomer?.phone || ''}
        customerId={waCustomer?.id}
        customerName={waCustomer ? `${waCustomer.first_name || ''} ${waCustomer.last_name || ''}`.trim() : ''}
      />
    </DragDropContext>
  );
};
